import { type ColumnDef, createColumnHelper } from '@tanstack/react-table';
import Tippy from '@tippyjs/react';
import React from 'react';
import { numericFormatter } from 'react-number-format';
import { formatDecimalSeparator } from 'utils/formatters/number/numberUtils';
import { removeUnderscoreAndCapitalize } from 'utils/formatters/string/stringFormatter';
import { getUtilityIcon } from 'utils/utilityTypeUtils/utilityTypeUtils';

export interface QuickWinOpportunity {
  id: number;
  energy_conservation_measure: number;
  analytic_name: string;
  investment_cost: string;
  payback_time_years: string;
}

interface QuickWinTableColumnsReturnType {
  columns: ColumnDef<QuickWinOpportunity, any>[];
}

const useGetQuickWinTableColumns = (): QuickWinTableColumnsReturnType => {
  const columnHelper = createColumnHelper<QuickWinOpportunity>();

  const columns: ColumnDef<QuickWinOpportunity, any>[] = [
    columnHelper.accessor('energy_conservation_measure', {
      header: () => 'Opportunity',
      cell: (info: any) => {
        const values = info.row.original;
        const formattedOpportunity = removeUnderscoreAndCapitalize(
          info.getValue()?.toString()
        );

        return (
          <Tippy content={formattedOpportunity} maxWidth={450}>
            <span className="flex items-center gap-2.5">
              <div className="fill-gray-50">
                {getUtilityIcon(values.utility_type)}
              </div>
              <span className="max-w-[300px] desktop-l:max-w-[400px] block text-ellipsis overflow-hidden truncate">
                {formattedOpportunity}
              </span>
            </span>
          </Tippy>
        );
      },
      size: 264,
    }),
    columnHelper.accessor('investment_cost', {
      header: () => 'Investment',
      cell: (info: any) => (
        <span className="text-sm text-body-primary">
          {numericFormatter((info.getValue() ?? 0).toFixed(2), {
            decimalSeparator: ',',
            thousandSeparator: '.',
            decimalScale: 2,
          })}{' '}
          €
        </span>
      ),
      size: 106,
    }),
    columnHelper.accessor('payback_time_years', {
      header: () => 'Payback',
      cell: (info: any) => (
        <span className="text-sm text-body-primary">
          {formatDecimalSeparator({
            value: Math.abs(info.getValue() ?? 0),
            decimalScale: 2,
          })}{' '}
          years
        </span>
      ),
      size: 93,
    }),
  ];

  return { columns };
};

export default useGetQuickWinTableColumns;
